import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { NAV_ITEMS, NAV_SERVICES, NAV_PRICING, NAV_ABOUT, NAV_LOCATIONS, cityLinksByCounty, activeNavItem } from "@/data/nav";

/**
 * Phone menu (SEO audit batch 2, Part 4): the same five sections as the desktop
 * menu, as tap-to-expand groups. Groups are collapsed with CSS (max-height),
 * never conditionally rendered. The section that owns the current page starts
 * open.
 */
const ROW = "flex min-h-12 items-center rounded-md px-3 text-base font-medium text-[#0E1F3A] hover:bg-secondary active:bg-secondary";
const SUB_HEADING = "px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-500";

const Rows = ({ links, prefix }) => (
  <ul>
    {links.map((l) => (
      <li key={l.to}>
        <Link to={l.to} className={ROW} data-testid={`${prefix}-${l.to.split("/").filter(Boolean).join("-") || "home"}`}>
          {l.label}
        </Link>
      </li>
    ))}
  </ul>
);

const ServicesGroup = () => (
  <>
    {[NAV_SERVICES.homeowners, NAV_SERVICES.builders].map((col) => (
      <div key={col.heading}>
        <p className={SUB_HEADING}>{col.heading}</p>
        <Rows links={col.links} prefix="mobile-nav" />
      </div>
    ))}
    <Link to={NAV_SERVICES.all.to} className={`${ROW} font-semibold text-[#1D4ED8]`} data-testid="mobile-nav-services-all">
      {NAV_SERVICES.all.label} →
    </Link>
  </>
);

const LocationsGroup = () => (
  <>
    {NAV_LOCATIONS.counties.map((county) => (
      <div key={county.id}>
        <p className={SUB_HEADING}>{county.name}</p>
        <ul className="columns-2 gap-x-2">
          {cityLinksByCounty(county.id, { testIdPrefix: "mobile-area" }).map((c) => (
            <li key={c.key} className="break-inside-avoid">
              <Link to={c.to} className={`${ROW} text-sm`} data-testid={c.testId}>
                {c.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    ))}
    <p className={SUB_HEADING}>Also serving</p>
    <ul>
      {NAV_LOCATIONS.alsoServing.map((a) => (
        <li key={a.key}>
          <Link to={a.to} className={ROW} data-testid={`mobile-area-${a.key}`}>
            {a.label}
          </Link>
        </li>
      ))}
    </ul>
    <Link to={NAV_LOCATIONS.all.to} className={`${ROW} font-semibold text-[#1D4ED8]`} data-testid="mobile-nav-locations-all">
      {NAV_LOCATIONS.all.label} →
    </Link>
  </>
);

const GROUPS = {
  services: ServicesGroup,
  locations: LocationsGroup,
  pricing: () => <Rows links={NAV_PRICING} prefix="mobile-nav" />,
  about: () => <Rows links={NAV_ABOUT} prefix="mobile-nav" />,
};

const Group = ({ item, active, open, onToggle }) => {
  const Body = GROUPS[item.id];
  const id = `mobile-group-${item.id}`;
  return (
    <li className="border-b border-border" data-testid={`mobile-group-${item.id}`} data-open={open ? "true" : "false"}>
      <div className="flex items-center">
        <Link
          to={item.to}
          className={`${ROW} flex-1 ${active ? "text-[#1D4ED8]" : ""}`}
          aria-current={active ? "true" : undefined}
          data-testid={`mobile-nav-${item.id}`}
        >
          {item.label}
        </Link>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={id}
          aria-label={`${open ? "Collapse" : "Expand"} ${item.label}`}
          className="flex h-12 w-12 items-center justify-center rounded-md text-slate-500 hover:bg-secondary"
          data-testid={`mobile-group-${item.id}-toggle`}
        >
          <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      </div>
      <div
        id={id}
        className={`overflow-hidden pl-2 transition-[max-height] duration-300 ${open ? "max-h-[200rem] pb-3" : "max-h-0"}`}
        data-expanded={open ? "true" : "false"}
      >
        <Body />
      </div>
    </li>
  );
};

export const MobileNav = ({ pathname }) => {
  const active = activeNavItem(pathname);
  const [openId, setOpenId] = useState(active);
  return (
    <nav aria-label="Mobile navigation" data-testid="mobile-nav">
      <ul>
        {NAV_ITEMS.map((item) =>
          GROUPS[item.id] ? (
            <Group
              key={item.id}
              item={item}
              active={active === item.id}
              open={openId === item.id}
              onToggle={() => setOpenId((cur) => (cur === item.id ? null : item.id))}
            />
          ) : (
            <li key={item.id} className="border-b border-border" data-testid={`mobile-group-${item.id}`}>
              <Link to={item.to} className={`${ROW} ${active === item.id ? "text-[#1D4ED8]" : ""}`} aria-current={active === item.id ? "true" : undefined} data-testid={`mobile-nav-${item.id}`}>
                {item.label}
              </Link>
            </li>
          )
        )}
      </ul>
    </nav>
  );
};
